import React, { useCallback, useRef, useState } from "react";
import i18next from "i18next";
import useTranslation from "../../../hooks/useTranslation";
import FileRow from "./FileRow";
import type { FileItem } from "./FileRow";

const UploadIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className={className}>
    <path d="M14 18V6m0 0l-5 5m5-5l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 18v3a2 2 0 002 2h14a2 2 0 002-2v-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export interface FormFileUploaderProps {
  accept?: string;
  maxSize?: number;           // bytes, default 10MB
  maxFiles?: number;
  multiple?: boolean;
  disabled?: boolean;
  files?: FileItem[];
  onFilesChange?: (files: FileItem[]) => void;
  variant?: "default" | "compact" | "image-preview";
  label?: string;
  labels?: {
    dragAndDrop?: string; browse?: string; or?: string;
    formatHint?: string; addMore?: string; dropHere?: string;
    releaseToUpload?: string; disabled?: string;
  };
}

const validateFile = (file: File, accept?: string, maxSize?: number): string | undefined => {
  if (maxSize && file.size > maxSize)
    return i18next.t("common:fileUploader.tooLarge", { size: Math.round(maxSize / (1024 * 1024)) }) as string;
  if (accept) {
    const types = accept.split(",").map((t) => t.trim().toLowerCase());
    const ext = `.${file.name.toLowerCase().split(".").pop()}`;
    const mime = file.type.toLowerCase();
    const ok = types.some((t) => {
      if (t.startsWith(".")) return ext === t;
      if (t.endsWith("/*")) return mime.startsWith(t.replace("/*", "/"));
      return mime === t;
    });
    if (!ok) return i18next.t("common:fileUploader.unsupportedType") as string;
  }
  return undefined;
};

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("Read failed"));
    r.readAsDataURL(file);
  });

/**
 * Tier 2 composable file uploader — drop zone + file list + validation.
 * Built on useFormFileUpload hook. Drop-in upgrade for FormFileUpload with
 * 8 visual states: empty, drag-hover, uploading, uploaded, error, image-preview, disabled, compact/phone.
 */
const FormFileUploader: React.FC<FormFileUploaderProps> = ({
  accept, maxSize = 10 * 1024 * 1024, maxFiles, multiple = true,
  disabled = false, files = [], onFilesChange, variant = "default", label, labels,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isPhone = typeof window !== "undefined" && window.innerWidth < 768;
  const isCompact = variant === "compact" || isPhone;

  // Defaults come from common:fileUploader.* so a caller that passes no `labels`
  // still renders in the UI language (USER-LANG-LEFTOVERS-1).
  const { translate } = useTranslation("common");
  const txt = {
    dragAndDrop: labels?.dragAndDrop || translate("fileUploader.dragAndDrop"),
    browse: labels?.browse || translate("fileUploader.browse"),
    or: labels?.or || translate("fileUploader.or"),
    formatHint: labels?.formatHint || translate("fileUploader.formatHint"),
    addMore: labels?.addMore || translate("fileUploader.addMore"),
    dropHere: labels?.dropHere || translate("fileUploader.dropHere"),
    releaseToUpload: labels?.releaseToUpload || translate("fileUploader.releaseToUpload"),
    disabled: labels?.disabled || translate("fileUploader.disabled"),
  };

  const processIncomingFiles = useCallback(async (incoming: File[]) => {
    let batch = incoming;
    if (maxFiles) {
      const remaining = maxFiles - files.length;
      if (remaining <= 0) return;
      batch = incoming.slice(0, remaining);
    }
    const items: FileItem[] = await Promise.all(
      batch.map(async (f) => {
        const error = validateFile(f, accept, maxSize);
        let content: string | undefined;
        if (!error) { try { content = await readAsDataUrl(f); } catch { /* noop */ } }
        return { name: f.name, size: f.size, type: f.type, file: f, content, error };
      }),
    );
    onFilesChange?.([...files, ...items]);
  }, [files, onFilesChange, accept, maxSize, maxFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); if (!disabled) setIsDragOver(true);
  }, [disabled]);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    if (!disabled && e.dataTransfer.files.length > 0)
      processIncomingFiles(Array.from(e.dataTransfer.files));
  }, [disabled, processIncomingFiles]);

  const handleClick = useCallback(() => { if (!disabled) fileInputRef.current?.click(); }, [disabled]);
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0)
      processIncomingFiles(Array.from(e.target.files));
    e.target.value = "";
  }, [processIncomingFiles]);

  const handleRemove = useCallback((i: number) => {
    onFilesChange?.(files.filter((_, idx) => idx !== i));
  }, [files, onFilesChange]);
  const handleDownload = useCallback((file: FileItem) => {
    const href = file.url || file.content;
    if (href) window.open(href, "_blank");
  }, []);

  // Drop zone styling
  const dzBase = "flex flex-col items-center justify-center gap-1.5 rounded-card cursor-pointer transition-all duration-200";
  const dzH = isCompact ? "min-h-[120px]" : "min-h-[140px]";
  const dzBorder = isDragOver
    ? "border-2 border-solid border-mid-terracotta"
    : "border-[1.5px] border-dashed border-mid-border";
  const dzBg = isDragOver
    ? "bg-[#FDF5F2]"
    : "bg-[#FAFAF8] hover:border-mid-terracotta hover:bg-[#FDF5F2]";

  const hasFiles = files.length > 0;

  return (
    <div
      className={disabled ? "opacity-50 pointer-events-none cursor-not-allowed" : ""}
      role="group" aria-label={label || translate("fileUploader.groupLabel")}
    >
      {label && (
        <p className="font-sora text-[12px] font-bold text-mid-muted uppercase tracking-wider mb-2">{label}</p>
      )}

      {/* Empty drop zone */}
      {!hasFiles && (
        <div
          className={`${dzBase} ${dzH} ${dzBorder} ${dzBg}`}
          onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
          onClick={handleClick} onKeyDown={(e) => e.key === "Enter" && handleClick()}
          role="button" tabIndex={disabled ? -1 : 0}
          aria-label={translate("fileUploader.areaLabel")}
        >
          <UploadIcon className={`${isDragOver ? "text-mid-terracotta" : "text-mid-muted"} transition-colors`} />
          {isDragOver ? (
            <>
              <span className="font-sora text-[14px] font-semibold text-mid-terracotta">{txt.dropHere}</span>
              <span className="font-sora text-[12px] text-mid-terracotta">{txt.releaseToUpload}</span>
            </>
          ) : isCompact ? (
            <span className="font-sora text-[14px] font-medium text-mid-terracotta">{txt.browse}</span>
          ) : (
            <>
              <span className="font-sora text-[14px] text-mid-body">{txt.dragAndDrop}</span>
              <span className="font-sora text-[12px] text-mid-muted">{txt.or}</span>
              <button type="button" tabIndex={-1}
                className="font-sora text-[14px] text-mid-terracotta hover:underline focus:outline-none focus:ring-2 focus:ring-mid-terracotta focus:ring-offset-2 rounded-btn"
                onClick={(e) => { e.stopPropagation(); handleClick(); }}
              >{txt.browse}</button>
            </>
          )}
        </div>
      )}

      {/* File list + add-more (with drag support) */}
      {hasFiles && (
        <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className="contents">
          <div className="flex flex-col gap-2">
            {files.map((file, i) => (
              <FileRow key={`${file.name}-${file.id || i}`} file={file} index={i}
                variant={variant} isPhone={isCompact}
                onRemove={handleRemove} onDownload={handleDownload} />
            ))}
          </div>
          {(!maxFiles || files.length < maxFiles) && (
            <button type="button" onClick={handleClick}
              className="w-full mt-2 py-3 border-[1.5px] border-dashed border-mid-border rounded-card
                font-sora text-[13px] text-mid-muted hover:border-mid-terracotta hover:text-mid-terracotta
                hover:bg-[#FDF5F2] transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-mid-terracotta focus:ring-offset-2"
            >{txt.addMore}</button>
          )}
        </div>
      )}

      {/* Format hint */}
      {!hasFiles && (
        <p className="font-sora text-[11px] text-mid-muted text-center mt-2">
          {disabled ? txt.disabled : txt.formatHint}
        </p>
      )}

      <input ref={fileInputRef} type="file" multiple={multiple} accept={accept}
        className="hidden" onChange={handleInputChange} tabIndex={-1} aria-hidden="true" />
    </div>
  );
};

export default FormFileUploader;
export type { FileItem };
